/* eslint-disable react/no-unknown-property */
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { forwardRef, useRef, useMemo, useEffect } from 'react';
import { Color } from 'three';
import PropTypes from 'prop-types';

// Convert hex color to normalized RGB for Three.js
const hexToNormalizedRGB = hex => {
  hex = hex.replace('#', '');
  return [
    Number.parseInt(hex.slice(0, 2), 16) / 255,
    Number.parseInt(hex.slice(2, 4), 16) / 255,
    Number.parseInt(hex.slice(4, 6), 16) / 255
  ];
};

// Vertex shader - handles position and UV mapping
const vertexShader = `
varying vec2 vUv;
varying vec3 vPosition;

void main() {
  vPosition = position;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// Fragment shader - creates the flowing fabric effect with noise and pattern
const fragmentShader = `
varying vec2 vUv;
varying vec3 vPosition;

uniform float uTime;
uniform vec3  uColor;
uniform float uSpeed;
uniform float uScale;
uniform float uRotation;
uniform float uNoiseIntensity;

const float e = 2.71828182845904523536;

// Simple noise function for grain effect
float noise(vec2 texCoord) {
  float G = e;
  vec2  r = (G * sin(G * texCoord));
  return fract(r.x * r.y * (1.0 + texCoord.x));
}

// Rotate UV coordinates for pattern rotation
vec2 rotateUvs(vec2 uv, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  mat2  rot = mat2(c, -s, s, c);
  return rot * uv;
}

void main() {
  float rnd        = noise(gl_FragCoord.xy);
  vec2  uv         = rotateUvs(vUv * uScale, uRotation);
  vec2  tex        = uv * uScale;
  float tOffset    = uSpeed * uTime;

  tex.y += 0.03 * sin(8.0 * tex.x - tOffset);

  float pattern = 0.6 +
                  0.4 * sin(5.0 * (tex.x + tex.y +
                                   cos(3.0 * tex.x + 5.0 * tex.y) +
                                   0.02 * tOffset) +
                           sin(20.0 * (tex.x + tex.y - 0.1 * tOffset)));

  vec4 col = vec4(uColor, 1.0) * vec4(pattern) - rnd / 15.0 * uNoiseIntensity;
  col.a = 1.0;
  gl_FragColor = col;
}
`;

// SilkPlane component - handles the mesh and shader material
const SilkPlane = forwardRef(function SilkPlane({ uniforms }, ref) {
  const { viewport } = useThree();

  // Update mesh scale to match viewport dimensions
  useEffect(() => {
    if (ref.current) {
      ref.current.scale.set(viewport.width, viewport.height, 1);
    }
  }, [ref, viewport]);

  // Update time uniform in animation loop - no React re-renders
  useFrame((_, delta) => {
    if (ref.current?.material?.uniforms) {
      ref.current.material.uniforms.uTime.value += 0.1 * delta;
    }
  });

  return (
    <mesh ref={ref}>
      <planeGeometry args={[1, 1, 1, 1]} />
      <shaderMaterial uniforms={uniforms} vertexShader={vertexShader} fragmentShader={fragmentShader} />
    </mesh>
  );
});
SilkPlane.displayName = 'SilkPlane';

// Main Silk component - handles props and uniform updates
const Silk = ({ 
  speed = 5, 
  scale = 1, 
  color = '#7B7481', 
  noiseIntensity = 1.5, 
  rotation = 0,
  className = '',
  style = {}
}) => {
  const meshRef = useRef();

  // Create static uniforms object - reference never changes
  const uniforms = useMemo(
    () => ({
      uSpeed: { value: speed },
      uScale: { value: scale },
      uNoiseIntensity: { value: noiseIntensity },
      uColor: { value: new Color(...hexToNormalizedRGB(color)) },
      uRotation: { value: rotation },
      uTime: { value: 0 }
    }),
    [] // Empty dependency array - uniforms object created once
  );

  // Update uniform values directly when props change
  // This avoids recreating the uniforms object and ensures Three.js sees the updates
  useEffect(() => {
    if (meshRef.current?.material?.uniforms) {
      meshRef.current.material.uniforms.uSpeed.value = speed;
      meshRef.current.material.uniforms.uScale.value = scale;
      meshRef.current.material.uniforms.uNoiseIntensity.value = noiseIntensity;
      meshRef.current.material.uniforms.uColor.value.set(...hexToNormalizedRGB(color));
      meshRef.current.material.uniforms.uRotation.value = rotation;
    }
  }, [speed, scale, noiseIntensity, color, rotation]);

  return (
    <div 
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: -1,
        pointerEvents: 'none',
        ...style
      }}
    >
      <Canvas dpr={[1, 2]} frameloop="always">
        <SilkPlane ref={meshRef} uniforms={uniforms} />
      </Canvas>
    </div>
  );
};

export default Silk;

Silk.propTypes = {
  speed: PropTypes.number,
  scale: PropTypes.number,
  color: PropTypes.string,
  noiseIntensity: PropTypes.number,
  rotation: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
};

SilkPlane.propTypes = {
  uniforms: PropTypes.object.isRequired,
};
